import React, { useState } from 'react'
import { Link, withRouter } from 'react-router-dom'
import styles from '../style.module.css'
import { getSelectedClients } from '../../../data/interactions'
import { usePricingStore } from '../pricingStore'
import { lockedInputStyle } from './market/MarketMasterEditor'

const MarketList = ({ match, history, location }) => {
	const {
		markets,
		loadingMarkets,
		deleteMarket,
		upsertActiveMarketPricing,
		activePricingType,
		setActivePricingType,
	} = usePricingStore()

	const [showAddForm, setShowAddForm] = useState(false)
	const [createStatus, setCreateStatus] = useState('idle')
	const [createError, setCreateError] = useState('')
	const [newMarketForm, setNewMarketForm] = useState({
		market: '',
		currency: '',
		licensePrice: '',
		compensation: '',
		furtherDamages: '',
		administrativeFee: '',
		manipulatedFee: '',
		ceaseAndDesistFee: '',
		reminderOneFee: '',
		reminderTwoFee: '',
		finalWarningFee: '',
	})

	const updateNew = (field, value) =>
		setNewMarketForm((f) => ({ ...f, [field]: value }))

	const submitNewMarket = async () => {
		setCreateError('')
		if (createStatus === 'saving') return
		setCreateStatus('saving')
		try {
			await upsertActiveMarketPricing(newMarketForm.market, newMarketForm)
			setCreateStatus('saved')
			setTimeout(() => setCreateStatus('idle'), 2000)
			setNewMarketForm({
				market: '',
				currency: '',
				licensePrice: '',
				compensation: '',
				furtherDamages: '',
				administrativeFee: '',
				manipulatedFee: '',
				ceaseAndDesistFee: '',
				reminderOneFee: '',
				reminderTwoFee: '',
				finalWarningFee: '',
			})
			setShowAddForm(false)
		} catch (e) {
			console.error(e)
			setCreateError(e.message || 'Failed to create market')
			setCreateStatus('error')
			setTimeout(() => setCreateStatus('idle'), 4000)
		}
	}

	const selectedClient = getSelectedClients()[0]

	const onDelete = async (market) => {
		if (
			!window.confirm(
				`Delete ${activePricingType} pricing for ${market} and its client overrides?`,
			)
		)
			return
		try {
			await deleteMarket(market)
			if (
				/\/markets\//.test(location.pathname) &&
				location.pathname.endsWith(`/${market}`)
			) {
				history.replace(match.path)
			}
		} catch (e) {
			alert('Failed to delete market pricing')
		}
	}

	return (
		<div className={styles.marketList}>
			<h3 style={{ marginTop: 0 }}>Market Pricing</h3>
			<div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
				{['COMMERCIAL', 'EDITORIAL'].map((t) => (
					<button
						key={t}
						className={styles.saveButton}
						style={{
							background: t === activePricingType ? '#007bff' : '#6c757d',
						}}
						onClick={() => setActivePricingType(t)}
					>
						{t}
					</button>
				))}
			</div>
			{loadingMarkets && <div>Loading markets...</div>}
			{!loadingMarkets && markets.length === 0 && (
				<div>No markets defined yet for {activePricingType}.</div>
			)}
			<div>
				{markets.map((m) => (
					<Link
						key={m.market}
						to={`${match.path}/markets/${m.market}`}
						style={{ textDecoration: 'none', color: 'inherit' }}
					>
						<div className={styles.marketRow}>
							<div style={{ flex: 1 }}>
								<strong>{m.market}</strong>{' '}
								<span style={{ fontSize: 11 }}>{m.currency}</span>
							</div>
							<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
								<span style={{ fontSize: 12 }}>
									{(Number(m.licensePrice) || 0) +
										(Number(m.compensation) || 0) +
										(Number(m.furtherDamages) || 0)}{' '}
									{m.currency}
								</span>
								<button
									className={styles.saveButton}
									style={{ background: '#d9534f' }}
									onClick={() => onDelete(m.market)}
								>
									Delete
								</button>
							</div>
						</div>
					</Link>
				))}
			</div>
			<div style={{ marginTop: 15 }}>
				{!showAddForm && (
					<button
						className={styles.saveButton}
						style={{ background: '#28a745' }}
						onClick={() => setShowAddForm(true)}
					>
						Add Market ({activePricingType})
					</button>
				)}
				{showAddForm && (
					<div
						style={{ border: '1px solid #eee', padding: 10, borderRadius: 4 }}
					>
						<h4 style={{ marginTop: 0 }}>New Market ({activePricingType})</h4>
						<div className={styles.formGroup}>
							<label>Market Code *</label>
							<input
								value={newMarketForm.market}
								onChange={(e) =>
									updateNew('market', e.target.value.toUpperCase())
								}
								placeholder='e.g. BE'
								maxLength={2}
							/>
						</div>
						<div className={styles.formGroup}>
							<label>Currency *</label>
							<input
								value={newMarketForm.currency}
								onChange={(e) =>
									updateNew('currency', e.target.value.toUpperCase())
								}
								placeholder='e.g. EUR'
								maxLength={3}
							/>
						</div>
						<div className={styles.formGroup}>
							<label>License Price *</label>
							<input
								value={newMarketForm.licensePrice}
								onChange={(e) => updateNew('licensePrice', e.target.value)}
								type='number'
								min='0'
								step='0.01'
							/>
						</div>
						<div className={styles.formGroup}>
							<label>Compensation</label>
							<input
								value={newMarketForm.compensation}
								onChange={(e) => updateNew('compensation', e.target.value)}
								type='number'
								min='0'
								step='0.01'
							/>
						</div>
						<div className={styles.formGroup}>
							<label>Further Damages</label>
							<input
								value={newMarketForm.furtherDamages}
								onChange={(e) => updateNew('furtherDamages', e.target.value)}
								type='number'
								min='0'
								step='0.01'
							/>
						</div>
						<div className={styles.formGroup}>
							<label>Administrative Fee (locked)</label>
							<input
								value={newMarketForm.administrativeFee}
								readOnly
								title='Administrative Fee cannot be edited'
								style={lockedInputStyle}
								type='number'
								min='0'
								step='0.01'
							/>
						</div>
						<div className={styles.formGroup}>
							<label>Manipulated Fee</label>
							<input
								value={newMarketForm.manipulatedFee}
								onChange={(e) => updateNew('manipulatedFee', e.target.value)}
								type='number'
								min='0'
								step='0.01'
							/>
						</div>
						<div className={styles.formGroup}>
							<label>Cease And Desist Fee</label>
							<input
								value={newMarketForm.ceaseAndDesistFee}
								onChange={(e) => updateNew('ceaseAndDesistFee', e.target.value)}
								type='number'
								min='0'
								step='0.01'
							/>
						</div>
						<div className={styles.formGroup}>
							<label>Reminder One Fee</label>
							<input
								value={newMarketForm.reminderOneFee}
								onChange={(e) => updateNew('reminderOneFee', e.target.value)}
								type='number'
								min='0'
								step='0.01'
							/>
						</div>
						<div className={styles.formGroup}>
							<label>Final Warning Fee</label>
							<input
								value={newMarketForm.finalWarningFee}
								onChange={(e) => updateNew('finalWarningFee', e.target.value)}
								type='number'
								min='0'
								step='0.01'
							/>
						</div>
						<div className={styles.actions}>
							<button
								className={styles.saveButton}
								onClick={submitNewMarket}
								disabled={createStatus === 'saving'}
							>
								Create
							</button>
							<button
								className={styles.saveButton}
								style={{ background: '#6c757d' }}
								onClick={() => {
									setShowAddForm(false)
									setCreateError('')
								}}
								disabled={createStatus === 'saving'}
							>
								Cancel
							</button>
							{createStatus === 'saving' && <span>Saving...</span>}
							{createStatus === 'saved' && (
								<span style={{ color: 'green' }}>Created!</span>
							)}
							{createStatus === 'error' && (
								<span style={{ color: 'red' }}>{createError}</span>
							)}
						</div>
					</div>
				)}
			</div>
			{selectedClient && (
				<div style={{ marginTop: 20 }}>
					<h4 style={{ marginBottom: 6 }}>Partner Pricing</h4>
					<Link to={`${match.path}/clients/${selectedClient.client_id}`}>
						<button className={styles.saveButton}>
							Edit {selectedClient.short_name} pricing
						</button>
					</Link>
				</div>
			)}
		</div>
	)
}

export default withRouter(MarketList)
